// Home — operations feed (Session A rebuild).
//
// Replaces the stat-tiles + users-table dashboard with a live view of the
// portal's actual work. Uses EXISTING endpoints only:
//   - admin/dashboard          (counts — same call as the old dashboard)
//   - admin/incident           (list — same endpoint/query shape AgTable uses)
//   - admin/user               (list — same endpoint the old AgTable hit)
// "Needs attention" is derived client-side from the incident list; the API
// has no dedicated endpoint for it (logged as an API wish in the session
// report).
import { Fragment, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

import AgStatus from "elements/System/AgStatus";
import {
  FwPanel,
  FwEmpty,
  StatStrip,
  StatItem,
  ActivityRow,
  AttentionRow,
  deriveAttention,
  FeedIncident,
  FeedUser,
  UserRow,
} from "elements/Dashboard/OpsComponents";
import { api, common } from "includes";

export default function Home() {
  const permission: any = useOutletContext();
  const isAuth = common.checkAuth();

  const [process, setProcess] = useState<any>({ result: 100 });
  const [authData, setAuthData] = useState<any>(common.getAuth());
  const [counts, setCounts] = useState<any>(null);
  const [incidents, setIncidents] = useState<FeedIncident[]>([]);
  const [users, setUsers] = useState<FeedUser[]>([]);

  useEffect(() => {
    if (isAuth) {
      setAuthData(common.getAuth());
      if (!permission?.includes("hor")) {
        common.redirect("app/home");
      } else {
        getData();
      }
    }
  }, []);

  // api
  const getData = () => {
    processState("result", 100);
    // counts — identical call to the previous dashboard
    api.call({ url: "admin/dashboard", method: "GET" }, (res: any) => {
      processState("result", res.status);
      if (res.status === 200) {
        setCounts(res.data.data);
      }
    });

    // recent incident activity — same endpoint + query shape AgTable uses
    if (permission?.includes("inr")) {
      api.call(
        {
          url: "admin/incident",
          method: "GET",
          query: `?sortBy=createdAt&sortDir=desc&offset=1&limit=12&query=${JSON.stringify({})}`,
        },
        (res: any) => {
          if (res.status === 200) {
            setIncidents(res.data?.data || []);
          }
        }
      );
    }

    // newest users — same endpoint the old dashboard's AgTable hit
    if (authData?.role === "super") {
      api.call(
        {
          url: "admin/user",
          method: "GET",
          query: `?sortBy=createdAt&sortDir=desc&offset=1&limit=6&query=${JSON.stringify({})}`,
        },
        (res: any) => {
          if (res.status === 200) {
            setUsers(res.data?.data || []);
          }
        }
      );
    }
  };

  // callback
  const onAgCallBack = () => {
    getData();
  };

  // support
  const processState = (label: any, value: any) => {
    setProcess((prev: any) => ({ ...prev, [label]: value }));
  };

  const stats: StatItem[] = counts
    ? [
        {
          label: "Incidents",
          value: counts.noOfIncident,
          to: permission?.includes("inr") ? "/incident/list" : undefined,
        },
        {
          label: "Users",
          value: counts.noOfUser,
          to: permission?.includes("usr") ? "/user/list" : undefined,
        },
        {
          label: "Localities",
          value: counts.noOfLocality,
          to: permission?.includes("lor") ? "/locality/list" : undefined,
        },
        {
          label: "Sub Localities",
          value: counts.noOfSubLocality,
          to: permission?.includes("slr") ? "/sublocality/list" : undefined,
        },
      ]
    : [];

  const attention = deriveAttention(incidents);

  return (
    <Fragment>
      {process.result != 200 ? (
        <AgStatus
          process={process.result}
          message={{
            100: "Loading... Please wait!",
            404: "Something went wrong! Please Refresh Again",
          }}
          button={{ 400: "Refresh", 404: "Refresh" }}
          size="l"
          onAgCallBack={onAgCallBack}
        />
      ) : (
        <Fragment>
          <div className="fw-dash__head">
            <div>
              <h1>Operations</h1>
              <p className="fw-dash__sub">
                Welcome back, {authData?.firstName || "admin"}
              </p>
            </div>
            <div className="fw-dash__actions">
              {permission?.includes("inc") && (
                <Link
                  to="/incident/form?mode=add"
                  className="fw-dash__new-incident"
                >
                  <i className="ri-add-line" />
                  New incident
                </Link>
              )}
            </div>
          </div>

          <StatStrip items={stats} />

          <div className="fw-dash">
            <div>
              {permission?.includes("inr") && (
                <FwPanel
                  title="Incident activity"
                  action={{ label: "View all", to: "/incident/list" }}
                >
                  {incidents.length === 0 ? (
                    <FwEmpty>No recent incidents.</FwEmpty>
                  ) : (
                    incidents.map((it) => <ActivityRow key={it._id} item={it} />)
                  )}
                </FwPanel>
              )}
            </div>

            <div>
              {permission?.includes("inr") && (
                <FwPanel title="Needs attention">
                  {attention.length === 0 ? (
                    <FwEmpty>Nothing flagged in recent incidents.</FwEmpty>
                  ) : (
                    attention.map((a) => <AttentionRow key={a.id} item={a} />)
                  )}
                </FwPanel>
              )}

              {authData?.role === "super" && (
                <FwPanel
                  title="Newest users"
                  action={{ label: "View all", to: "/user/list" }}
                >
                  {users.length === 0 ? (
                    <FwEmpty>No recent registrations.</FwEmpty>
                  ) : (
                    users.map((u) => <UserRow key={u._id} item={u} />)
                  )}
                </FwPanel>
              )}
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
}
